import {
  removeAlternatePinNameMarkup,
  renderAlternatePinNameMarkup,
} from './alternatePinCalloutSvg';
import { applyDefaultPublicPinoutCalloutColors } from './publicPinoutCalloutColors';
import { createSvgElementJpegDataUrl } from './svgExport';

const SVG_EXPORT_HOST_STYLE = {
  position: 'fixed',
  left: '-100000px',
  top: '-100000px',
  opacity: '0',
  pointerEvents: 'none',
  overflow: 'hidden',
  visibility: 'hidden',
};

const createHiddenSvgExportHost = () => {
  const host = document.createElement('div');
  Object.assign(host.style, SVG_EXPORT_HOST_STYLE);
  host.setAttribute('aria-hidden', 'true');
  return host;
};

const parseSvgMarkup = (svgMarkup) => {
  const normalizedMarkup = String(svgMarkup || '').trim();
  if (!normalizedMarkup) {
    return null;
  }

  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(normalizedMarkup, 'image/svg+xml');
  const parserError = parsedDocument.querySelector('parsererror');
  const svgElement = parsedDocument.documentElement;

  if (parserError || !svgElement || svgElement.nodeName.toLowerCase() !== 'svg') {
    throw new Error('Public pinout image export requires valid SVG markup.');
  }

  return svgElement;
};

const DEFAULT_PUBLIC_PINOUT_IMAGE_FILE_NAME = 'board-pinout.jpg';

const normalizePublicPinoutImageFileName = (value) => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) {
    return DEFAULT_PUBLIC_PINOUT_IMAGE_FILE_NAME;
  }

  return /\.jpe?g$/i.test(normalizedValue)
    ? normalizedValue.replace(/\.jpeg$/i, '.jpg')
    : `${normalizedValue.replace(/\.[^.]+$/, '')}.jpg`;
};

const dataUrlToBlob = (dataUrl) => {
  const normalizedValue = String(dataUrl || '').trim();
  const matches = normalizedValue.match(/^data:([^;,]+);base64,(.+)$/s);

  if (!matches) {
    throw new Error('Public pinout image export produced an invalid image payload.');
  }

  const mimeType = matches[1] || 'image/jpeg';
  const binary = atob(matches[2]);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: mimeType });
};

export const createBoardPublicPinoutImageDataUrl = async ({
  svgMarkup,
  pins = [],
  showAlternatePinNames = true,
  showAlternatePinCalloutColors = true,
  activeFilterGroups = [],
  fileName = '',
} = {}) => {
  if (typeof document === 'undefined' || typeof DOMParser === 'undefined') {
    throw new Error('Public pinout image export is only available in the browser.');
  }

  const svgElement = parseSvgMarkup(svgMarkup);
  if (!(svgElement instanceof SVGElement)) {
    return '';
  }

  const exportHost = createHiddenSvgExportHost();
  document.body.appendChild(exportHost);
  exportHost.appendChild(svgElement);

  try {
    applyDefaultPublicPinoutCalloutColors(svgElement, pins);

    renderAlternatePinNameMarkup(svgElement, {
      pins,
      showAlternatePinNames,
      showAlternatePinCalloutColors,
      activeFilterGroups,
    });

    return await createSvgElementJpegDataUrl(svgElement, {
      fileName,
      expandToVisibleContent: true,
      outputQuality: 0.82,
    });
  } finally {
    removeAlternatePinNameMarkup(svgElement);
    exportHost.remove();
  }
};

export const createBoardPublicPinoutImageUpload = async (options = {}) => {
  const fileName = normalizePublicPinoutImageFileName(options.fileName);
  const dataUrl = await createBoardPublicPinoutImageDataUrl({
    ...options,
    fileName,
  });

  if (!dataUrl) {
    return null;
  }

  return {
    blob: dataUrlToBlob(dataUrl),
    fileName,
  };
};
